import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { API_BASE, fallbackPortfolio } from '../data/fallback';

const Portfolio = () => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('products'); // 'products' | 'projects'
  const [filter, setFilter] = useState('ALL');              // 'ALL' | 'WEB' | 'APP'
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE}/api/portfolio`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data.success && data.data.length) {
          const sorted = [...data.data].sort((a, b) => (b.is_product || 0) - (a.is_product || 0));
          setPortfolioItems(sorted);
        } else {
          setPortfolioItems(fallbackPortfolio);
        }
      })
      .catch(() => setPortfolioItems(fallbackPortfolio))
      .finally(() => setLoading(false));
  }, []);

  // Products first, then by sort_order
  const products = portfolioItems.filter(p => p.is_product);
  const projects = portfolioItems.filter(p => !p.is_product);

  // Items with BOTH show in WEB and APP filters
  const applyFilter = (items) => {
    if (filter === 'ALL') return items;
    return items.filter(p =>
      p.category === filter || p.category === 'BOTH'
    );
  };

  const filteredProducts = applyFilter(products);
  const filteredProjects = applyFilter(projects);

  // Items shown based on primary toggle
  const showProducts = activeType === 'products';
  const activeItems  = showProducts ? filteredProducts : filteredProjects;

  const handleViewProduct = (e, item) => {
    e.stopPropagation();
    if (item.project_url && item.project_url !== '#') {
      const url = item.project_url.startsWith('http') ? item.project_url : `https://${item.project_url}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div>
      <style>{`
        /* ── Hero ── */
        .pf-hero {
          background: linear-gradient(135deg, #050516 0%, #1a1a5e 60%, #2d2d8e 100%);
          padding: 120px 0 70px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .pf-hero::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 60% 40%, rgba(0,212,255,0.08) 0%, transparent 60%);
        }

        /* ── Filter Bars ── */
        /* Primary: Products / Projects toggle */
        .pf-type-toggle {
          display: flex;
          justify-content: center;
          gap: 0;
          margin-bottom: 16px;
          background: #e9ecef;
          border-radius: 50px;
          padding: 4px;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
        .pf-type-btn {
          padding: 10px 32px;
          border-radius: 50px;
          border: none;
          background: transparent;
          color: #6c757d;
          font-weight: 700;
          font-size: 0.88rem;
          cursor: pointer;
          transition: all 0.25s ease;
          letter-spacing: 0.3px;
          display: flex; align-items: center; gap: 7px;
        }
        .pf-type-btn.active {
          background: white;
          color: #1a1a5e;
          box-shadow: 0 2px 8px rgba(0,0,0,0.12);
        }
        /* Secondary: All / Web / App */
        .pf-filters {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 48px;
          flex-wrap: wrap;
        }
        .pf-filter-btn {
          padding: 8px 22px;
          border-radius: 50px;
          border: 1.5px solid #dee2e6;
          background: white;
          color: #6c757d;
          font-weight: 600;
          font-size: 0.82rem;
          cursor: pointer;
          letter-spacing: 0.3px;
          transition: all 0.25s ease;
        }
        .pf-filter-btn.active, .pf-filter-btn:hover {
          background: #1a1a5e;
          border-color: #1a1a5e;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(26,26,94,0.25);
        }

        /* ── Section Label ── */
        .pf-section-label {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 32px;
        }
        .pf-section-label h3 {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1a1a5e;
          white-space: nowrap;
        }
        .pf-section-label h3 span { color: #ff6b35; }
        .pf-label-line {
          flex: 1;
          height: 2px;
          background: linear-gradient(90deg, #dee2e6, transparent);
        }

        /* ── PRODUCT CARDS (left image + right info, Techies style) ── */
        .pf-product-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 28px;
          margin-bottom: 70px;
        }
        .pf-product-card {
          background: white;
          border-radius: 18px;
          border: 1px solid #e9ecef;
          overflow: hidden;
          display: flex;
          transition: all 0.35s cubic-bezier(0.25,0.8,0.25,1);
          cursor: pointer;
          position: relative;
        }
        .pf-product-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 24px 60px rgba(26,26,94,0.14);
          border-color: transparent;
        }
        .pf-product-img-wrap {
          width: 48%;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
          min-height: 240px;
          background: #f0f2ff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pf-product-img-wrap img {
          width: 100%; height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
          transition: transform 0.4s ease;
          padding: 8px;
        }
        .pf-product-card:hover .pf-product-img-wrap img {
          transform: scale(1.03);
        }
        .pf-product-img-placeholder {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          font-size: 4rem;
        }
        .pf-product-info {
          flex: 1;
          padding: 24px 22px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 10px;
        }
        .pf-product-tags {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .pf-tag {
          padding: 4px 14px;
          border-radius: 50px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          border: 1.5px solid;
        }
        .pf-tag-product {
          color: #10b981;
          border-color: #10b981;
          background: rgba(16,185,129,0.07);
        }
        .pf-tag-web {
          color: #3b82f6;
          border-color: #3b82f6;
          background: rgba(59,130,246,0.07);
        }
        .pf-tag-app {
          color: #8b5cf6;
          border-color: #8b5cf6;
          background: rgba(139,92,246,0.07);
        }
        .pf-product-title {
          font-size: 1.08rem;
          font-weight: 800;
          color: #1a1a5e;
          line-height: 1.3;
          margin: 0;
        }
        .pf-product-desc {
          font-size: 0.82rem;
          color: #6c757d;
          line-height: 1.65;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .pf-product-tech {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 2px;
        }
        .pf-tech-chip {
          background: #f0f2ff;
          color: #1a1a5e;
          padding: 3px 10px;
          border-radius: 4px;
          font-size: 0.68rem;
          font-weight: 600;
        }
        .pf-product-actions {
          display: flex;
          gap: 10px;
          margin-top: 6px;
        }
        .pf-view-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 18px;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          border: none;
        }
        .pf-view-btn-outline {
          border: 2px solid #dee2e6;
          background: white;
          color: #495057;
        }
        .pf-view-btn-outline:hover {
          border-color: #1a1a5e;
          color: #1a1a5e;
          background: #f0f2ff;
        }
        .pf-view-btn-primary {
          background: linear-gradient(135deg, #1a1a5e, #2d2d8e);
          color: white;
          border: 2px solid transparent;
        }
        .pf-view-btn-primary:hover {
          background: linear-gradient(135deg, #ff6b35, #ff8c5a);
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(255,107,53,0.35);
        }
        .pf-view-btn-primary.disabled-btn {
          opacity: 0.5;
          cursor: not-allowed;
          pointer-events: none;
        }

        /* ── CLIENT PROJECT CARDS (3-col grid style) ── */
        .pf-project-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 70px;
        }
        .pf-project-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e9ecef;
          transition: all 0.35s ease;
          cursor: pointer;
        }
        .pf-project-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 18px 45px rgba(0,0,0,0.13);
          border-color: transparent;
        }
        .pf-project-img {
          height: 190px;
          position: relative;
          overflow: hidden;
        }
        .pf-project-img img {
          width: 100%; height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .pf-project-card:hover .pf-project-img img { transform: scale(1.06); }
        .pf-project-img-placeholder {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          font-size: 3.5rem;
        }
        .pf-project-overlay {
          position: absolute; inset: 0;
          background: rgba(26,26,94,0.82);
          display: flex; align-items: center; justify-content: center;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .pf-project-card:hover .pf-project-overlay { opacity: 1; }
        .pf-overlay-btn {
          color: white;
          font-size: 0.82rem;
          font-weight: 700;
          border: 2px solid white;
          padding: 8px 20px;
          border-radius: 50px;
          letter-spacing: 0.5px;
          transform: translateY(8px);
          transition: all 0.35s ease;
        }
        .pf-project-card:hover .pf-overlay-btn { transform: translateY(0); }
        .pf-project-body { padding: 18px; }
        .pf-project-meta {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 8px;
        }
        .pf-cat-badge {
          background: linear-gradient(135deg, #ff6b35, #ff8c5a);
          color: white; padding: 3px 11px;
          border-radius: 50px; font-size: 0.68rem;
          font-weight: 700; letter-spacing: 1px;
        }
        .pf-year { font-size: 0.75rem; color: #6c757d; }
        .pf-project-body h4 {
          font-size: 0.95rem; font-weight: 700;
          color: #1a1a5e; margin-bottom: 6px;
          line-height: 1.3;
        }
        .pf-project-body p {
          font-size: 0.8rem; color: #6c757d; line-height: 1.65;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* ── Empty State ── */
        .pf-empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px 20px;
          color: #adb5bd;
          font-size: 0.95rem;
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .pf-product-grid { grid-template-columns: 1fr; }
          .pf-project-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .pf-product-card { flex-direction: column; }
          .pf-product-img-wrap { width: 100%; min-height: 200px; }
          .pf-project-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Hero */}
      <div className="pf-hero">
        <div className="container">
          <AnimatedSection>
            <h1 className="page-hero-title">Our <span>Portfolio</span></h1>
            <p className="page-hero-subtitle">
              Products we built & projects we've delivered — explore our work across web and mobile
            </p>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <span>Portfolio</span>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* Main Content */}
      <section className="section-padding" style={{ background: '#f8f9fa' }}>
        <div className="container">

          {/* ── STEP 1: Primary type toggle ── */}
          <AnimatedSection>
            <div className="pf-type-toggle">
              {/* <button
                className={`pf-type-btn ${activeType === 'projects' ? 'active' : ''}`}
                onClick={() => { setActiveType('projects'); setFilter('ALL'); }}
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                Projects
                <span style={{ background: activeType==='projects'?'#e8603a':'#dee2e6', color: activeType==='projects'?'white':'#6c757d', fontSize:'0.68rem', fontWeight:800, padding:'1px 7px', borderRadius:'50px', marginLeft:2 }}>
                  {projects.length}
                </span>
              </button> */}
                <button
                className={`pf-type-btn ${activeType === 'products' ? 'active' : ''}`}
                onClick={() => { setActiveType('products'); setFilter('ALL'); }}
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                Products
                <span style={{ background: activeType==='products'?'#e8603a':'#dee2e6', color: activeType==='products'?'white':'#6c757d', fontSize:'0.68rem', fontWeight:800, padding:'1px 7px', borderRadius:'50px', marginLeft:2 }}>
                  {products.length}
                </span>
              </button>
            </div>

            {/* ── STEP 2: Category sub-filter ── */}
            <div className="pf-filters">
              {[
                { key: 'ALL', label: 'All' },
                { key: 'WEB', label: 'Web' },
                { key: 'APP', label: 'App' },
              ].map(f => (
                <button
                  key={f.key}
                  className={`pf-filter-btn ${filter === f.key ? 'active' : ''}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </AnimatedSection>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px', color: '#6c757d', fontSize: '1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#6c757d" strokeWidth="2" style={{animation:'spin 0.8s linear infinite'}}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>
              Loading portfolio...
              <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : (
            <>

              {/* ── PROJECTS ── */}
              {!showProducts && (
                <AnimatedSection>
                  <div className="pf-section-label">
                    <h3><span>Projects</span></h3>
                    <div className="pf-label-line" />
                    <span style={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {filteredProjects.length > 0 ? (
                    <div className="pf-project-grid">
                      {filteredProjects.map((item, i) => (
                        <AnimatedSection key={item.id} animation="reveal" delay={i * 60}>
                          <div className="pf-project-card" onClick={() => navigate(`/portfolio/${item.id}`)}>
                            <div className="pf-project-img" style={{ background: `linear-gradient(135deg, ${item.color || '#3498db'}33, ${item.color || '#3498db'}66)` }}>
                              {item.image_url ? (
                                <img src={item.image_url.startsWith('http') ? item.image_url : `${API_BASE}${item.image_url}`} alt={item.title} onError={e => { e.target.style.display = 'none'; }} />
                              ) : (
                                <div className="pf-project-img-placeholder">{item.icon}</div>
                              )}
                              <div className="pf-project-overlay"><span className="pf-overlay-btn">View Details</span></div>
                            </div>
                            <div className="pf-project-body">
                              <div className="pf-project-meta">
                                <span className="pf-cat-badge">
                                  {item.category === 'BOTH' ? 'WEB + APP' : item.category}
                                </span>
                                <span className="pf-year">{item.project_date || '2024'}</span>
                              </div>
                              <h4>{item.title}</h4>
                              <p>{item.description}</p>
                            </div>
                          </div>
                        </AnimatedSection>
                      ))}
                    </div>
                  ) : (
                    <div className="pf-empty">No projects match the selected filter.</div>
                  )}
                </AnimatedSection>
              )}
              {/* ── PRODUCTS ── */}
              {showProducts && (
                <AnimatedSection>
                  <div className="pf-section-label">
                    <h3>Our <span>Products</span></h3>
                    <div className="pf-label-line" />
                    <span style={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {filteredProducts.length > 0 ? (
                    <div className="pf-product-grid">
                      {filteredProducts.map((item, i) => (
                        <AnimatedSection key={item.id} animation="reveal" delay={i * 80}>
                          <div className="pf-product-card" onClick={() => navigate(`/portfolio/${item.id}`)}>
                            <div className="pf-product-img-wrap" style={{ background: `linear-gradient(135deg, ${item.color || '#3498db'}22, ${item.color || '#3498db'}44)` }}>
                              {item.image_url ? (
                                <img src={item.image_url.startsWith('http') ? item.image_url : `${API_BASE}${item.image_url}`} alt={item.title} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                              ) : null}
                              <div className="pf-product-img-placeholder" style={{ display: item.image_url ? 'none' : 'flex' }}>
                                {item.icon && item.icon.startsWith('/uploads/') ? (
                                  <img src={`${API_BASE}${item.icon}`} alt="icon" style={{ width:48, height:48, objectFit:'cover', borderRadius:10 }} onError={e=>e.target.style.display='none'} />
                                ) : (
                                  <span style={{ fontSize:'3rem' }}>{item.icon}</span>
                                )}
                              </div>
                            </div>
                            <div className="pf-product-info">
                              <div className="pf-product-tags">
                                <span className="pf-tag pf-tag-product">Product</span>
                                {item.category === 'BOTH' ? (
                                  <>
                                    <span className="pf-tag pf-tag-web">Website</span>
                                    <span className="pf-tag pf-tag-app">App</span>
                                  </>
                                ) : (
                                  <span className={`pf-tag ${item.category === 'APP' ? 'pf-tag-app' : 'pf-tag-web'}`}>
                                    {item.category === 'APP' ? 'Mobile App' : 'Website'}
                                  </span>
                                )}
                              </div>
                              <h3 className="pf-product-title">{item.title}</h3>
                              <p className="pf-product-desc">{item.description}</p>
                              {(item.tech_stack || []).length > 0 && (
                                <div className="pf-product-tech">
                                  {(item.tech_stack || []).slice(0, 4).map((t, ti) => <span key={ti} className="pf-tech-chip">{t}</span>)}
                                </div>
                              )}
                              <div className="pf-product-actions">
                                <button className="pf-view-btn pf-view-btn-outline" onClick={e => { e.stopPropagation(); navigate(`/portfolio/${item.id}`); }}>
                                  Details
                                </button>
                                <button className={`pf-view-btn pf-view-btn-primary${(!item.project_url || item.project_url === '#') ? ' disabled-btn' : ''}`} onClick={e => handleViewProduct(e, item)}>
                                  View Product
                                </button>
                              </div>
                            </div>
                          </div>
                        </AnimatedSection>
                      ))}
                    </div>
                  ) : (
                    <div className="pf-empty">No products match the selected filter.</div>
                  )}
                </AnimatedSection>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #1a1a5e, #050516)',
        padding: '80px 20px',
        textAlign: 'center',
      }}>
        <AnimatedSection>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', marginBottom: '16px' }}>
            Have a Project in <span style={{ color: '#ff6b35' }}>Mind?</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '550px', margin: '0 auto 32px', lineHeight: 1.8 }}>
            Let's discuss your idea and bring it to life. We'd love to add your project to our portfolio!
          </p>
          <Link to="/contact" className="btn btn-primary">🚀 Start Your Project</Link>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default Portfolio;
